import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import AddToList from '../components/AddToList.jsx'
import Artwork from '../components/Artwork.jsx'
import BlindControl from '../components/BlindControl.jsx'
import { LoadingState } from '../components/ErrorState.jsx'
import Histogram from '../components/Histogram.jsx'
import RatingSheet from '../components/RatingSheet.jsx'
import RevisionTimeline from '../components/RevisionTimeline.jsx'
import { api, ApiError, is404 } from '../lib/api.js'
import { duration } from '../lib/format.js'
import { METHOD_LABELS, STATE_LABELS, display } from '../lib/score.js'

/**
 * The song page — a lighter album page (PLAN §7).
 *
 * "Appears on" is built from tracklists we already hold, so it grows with the
 * catalog. It is a recording, not a track: the same performance on a single,
 * an album and a compilation is one page here, which is exactly why a song
 * rating attaches to the recording (DEC-2026-08-01-002).
 *
 * A song score and a track score are different sentences about the same
 * recording — "this song is a 9.3" and "on that album it is a 6.0" — and the
 * album page owns the second one.
 */
export default function Song() {
  const { id } = useParams()
  const [song, setSong] = useState(null)
  const [rating, setRating] = useState(null)
  const [error, setError] = useState(null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [sheetError, setSheetError] = useState(null)
  const [blindBusy, setBlindBusy] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    setSong(null)
    setRating(null)
    setError(null)
    api
      .song(id, { signal: controller.signal })
      .then(setSong)
      .catch((err) => {
        if (err.name !== 'AbortError') setError(err)
      })
    api
      .songRating(id, { signal: controller.signal })
      .then(setRating)
      .catch(() => {
        /* The song is still worth reading without its scores. */
      })
    return () => controller.abort()
  }, [id])

  useEffect(() => {
    if (song) document.title = `${song.title} — Sonologue`
    else if (is404(error)) document.title = 'Not found — Sonologue'
  }, [song, error])

  const setBlind = useCallback(
    async (blind) => {
      setBlindBusy(true)
      try {
        setRating(await api.setSongBlind(id, blind))
      } catch {
        // Quiet on purpose — see the same handler on the album page.
      } finally {
        setBlindBusy(false)
      }
    },
    [id],
  )

  const save = useCallback(async (call) => {
    setBusy(true)
    setSheetError(null)
    try {
      setRating(await call())
      setOpen(false)
    } catch (err) {
      setSheetError(err instanceof ApiError ? err.message : 'Could not save that just now.')
    } finally {
      setBusy(false)
    }
  }, [])

  // The album page's branch, on its sibling: a 404 is the only error that
  // means the record is not there, and it is the only one a retry cannot fix.
  if (error) {
    return (
      <div className="card">
        <div className="empty-state">
          <h1>{is404(error) ? 'No such song' : 'Not loaded'}</h1>
          <p>
            {error instanceof ApiError
              ? error.message
              : 'Sonologue could not load this song just now.'}
          </p>
          {is404(error) ? null : (
            <button
              type="button"
              className="btn btn-tonal"
              onClick={() => window.location.reload()}
            >
              Try again
            </button>
          )}
          <Link className="btn btn-quiet" to="/search">
            Back to search
          </Link>
        </div>
      </div>
    )
  }

  if (!song) return <LoadingState />

  const mine = rating?.mine || null
  const community = rating?.community || null
  const edits = mine ? Math.max(0, (mine.revision_count || 1) - 1) : 0

  return (
    <>
      <div className="section-header">
        <div>
          <div className="album-artist">{song.credit_name}</div>
          <h1 className="large-title">{song.title}</h1>
          <div className="album-facts">
            {[song.disambiguation, duration(song.length_ms)].filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>

      <div className="album-cta">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setSheetError(null)
            setOpen(true)
          }}
        >
          {mine ? `Your rating · ${display(mine.score)}` : 'Rate this song'}
        </button>
        {mine?.visibility === 'private' ? <span className="state-chip">private</span> : null}
      </div>

      {/* Its own row under the score, as on the album page — never inside
          `.album-cta`, which wraps. */}
      <AddToList
        className="mt-3"
        targetType="recording"
        targetPublicId={song.public_id}
        subject={[song.title, song.credit_name].filter(Boolean).join(' · ')}
      />

      {rating ? (
        <div className="stat-grid">
          <div className="card stat-cell">
            <div className="stat-value num">
              {community?.blind ? '·' : (display(community?.mean) ?? '—')}
            </div>
            <div className="stat-label">
              {community?.blind
                ? `Hidden · ${community.count} rating${community.count === 1 ? '' : 's'} so far`
                : community?.count
                  ? `Community · ${community.count} rating${community.count === 1 ? '' : 's'}`
                  : 'Community · no ratings yet'}
            </div>
          </div>
          {mine ? (
            <div className="card stat-cell">
              <div className="stat-value num mine">{display(mine.score)}</div>
              <div className="stat-label">
                You · {METHOD_LABELS[mine.method]}
                {edits ? ` · edited ${edits} time${edits === 1 ? '' : 's'}` : ''}
              </div>
              {mine.state ? (
                <span className="state-chip">{STATE_LABELS[mine.state]}</span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {mine ? (
        <div className="card card-pad timeline-card">
          <RevisionTimeline kind="song" id={id} revisionCount={mine.revision_count} />
        </div>
      ) : null}

      {community?.blind ? (
        <div className="card card-pad">
          <p className="footnote mb-0">
            Blind mode is on for this song. Rate it to see what everybody else thought.
          </p>
          <BlindControl
            blind
            override={rating?.blind_override}
            busy={blindBusy}
            onSet={setBlind}
          />
        </div>
      ) : (
        <>
          {community?.count ? (
            <>
              <div className="section-header">
                <h2 className="title-2">Rating distribution</h2>
              </div>
              <div className="card card-pad">
                <Histogram
                  data={community.histogram}
                  count={community.count}
                  mean={community.mean}
                  bucketWidth={community.bucket_width}
                />
              </div>
            </>
          ) : null}
          {rating ? (
            <div className="card card-pad">
              <BlindControl
                blind={false}
                override={rating?.blind_override}
                busy={blindBusy}
                onSet={setBlind}
              />
            </div>
          ) : null}
        </>
      )}

      <div className="deep-links" aria-label="Listen on external services">
        {song.deep_links.map((link) => (
          <a
            key={link.provider}
            className="deep-link"
            href={link.href}
            target="_blank"
            rel="noreferrer noopener"
          >
            Listen on {link.label} ↗
          </a>
        ))}
      </div>

      <div className="section-header">
        <h2 className="title-2">Appears on</h2>
      </div>

      {song.appears_on.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <h2>No albums yet</h2>
            <p>This song is in the catalog on its own. Fetch an album it is on to link them.</p>
          </div>
        </div>
      ) : (
        <div className="card hairline-list fade-up">
          {song.appears_on.map((album) => (
            <Link key={album.public_id} className="row" to={`/album/${album.public_id}`}>
              <Artwork
                art={album.artwork}
                title={album.title}
                artist={album.credit_name}
                size={44}
              />
              <div className="row-main">
                <div className="row-title">{album.title}</div>
                <div className="row-sub">
                  {[album.credit_name, album.year].filter(Boolean).join(' · ')}
                </div>
              </div>
              <span className="faint" aria-hidden="true">
                ›
              </span>
            </Link>
          ))}
        </div>
      )}

      <p className="caption mt-4">Metadata from MusicBrainz.</p>

      {open ? (
        <RatingSheet
          title={song.title}
          subtitle={song.credit_name}
          variant="song"
          initialScore={mine?.score ?? null}
          initialState={mine?.state || null}
          initialVisibility={mine?.visibility || 'public'}
          initialSubscores={mine?.subscores || null}
          initialSubscoreMode={mine?.subscore_mode || 'independent'}
          initialSubscoreWeights={mine?.subscore_weights || null}
          initialTags={mine?.tags || null}
          canRemove={Boolean(mine)}
          busy={busy}
          error={sheetError}
          onSave={(payload) => save(() => api.rateSong(id, payload))}
          onRemove={() => save(() => api.unrateSong(id))}
          onSuggestTag={(text) => api.suggestSongTag(id, text)}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  )
}
